<template>
  <div class="mt-0" style="max-height: calc(100vh / var(--app-zoom, 1) - 15rem); overflow: hidden;">
    <div class="flex flex-column gap-4 p-2">
      <div class="flex flex-row justify-content-end gap-3">
        <IframeBackButton @click="onBackButtonClick" />
        <CreateQueueButton :class="`js-${props.name}-btn`" />
      </div>
      <div ref="wrapper" class="embedded-component">
        <div v-if="!iframeLoaded" class="fadein animation-duration-500 flex justify-content-center align-content-center h-full px-8">
          <span class="flex align-items-center mb-8">
            <!-- <img alt="Vue logo" class="pi-spin" src="@/assets/logo5.png" width="100" height="100" /> -->
            <ProgressSpinner animationDuration=".5s" style="width: 150px; height: 150px" strokeWidth="1" />
          </span>
        </div>
        <div v-show="iframeLoaded" class="fadein animation-duration-500 w-full h-full">
          <!-- Render only once the content is ready so the iframe's first srcdoc
               is the real document. Binding srcdoc while it's still undefined makes
               the iframe load about:blank first, then navigate to the content — that
               second navigation adds a browser-history entry and "eats" the first
               Back press. -->
          <iframe v-if="iframeDoc" ref="iframe" id="_iframe" :srcdoc="iframeDoc" frameborder="0" width="100%"
            height="100%"></iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import $ from 'jquery';
import { useRoute, useRouter } from 'vue-router';
import { ref, watch, onMounted, onUnmounted, defineProps } from 'vue';
import { useAppStore } from '@/stores/app';
import { defineClient, websiteProxyUrl } from '@/services/api';
import QueueService from "@/services/queue";
import IframeBackButton from '@/components/IframeBackButton.vue';
import CreateQueueButton from '@/components/CreateQueueButton.vue';

// sessionStorage key for the deterministic iframe Back navigation stack.
const NAV_STACK_KEY = 'booth:iframeNavStack';
// Global hook the injected iframe script calls to navigate without reloading.
const IFRAME_NAV_HOOK = 'boothIframeNavigate';
// Global hook the injected iframe script calls once its content has been styled
// AND reflowed to the frame, so the shell can hide the spinner only then.
const IFRAME_READY_HOOK = 'boothIframeReady';
// Safety net: reveal the iframe even if the ready signal never arrives (e.g. the
// embedded page has no jQuery / the script throws before signalling).
const READY_FALLBACK_MS = 4000;

const iframe = ref(null);
const iframeSrc = ref(null);
const iframeDoc = ref();
const iframeLoaded = ref(false);
// Fallback timer that reveals the iframe if the ready signal never arrives.
let readyFallbackTimer = null;

// Called by the injected iframe script once its content is styled and reflowed.
const markIframeReady = () => {
  clearTimeout(readyFallbackTimer);
  iframeLoaded.value = true;
};

const removeParents = ref([
  {
    element: '.sub-product > .inner-sub-product > a[href*="/custom-optics/filters/"]',
    parent: 'sub-product',
  }
]);

const route = useRoute();
const router = useRouter();
const apiClient = defineClient();
const appStore = useAppStore();

// Reload the iframe content (SPA) whenever the ?link= query changes, instead
// of doing a full document navigation — a reload would drop fullscreen mode.
watch(() => route.query.link, () => initializeIframe());

const props = defineProps({
  url: {
    type: String,
    default: () => '',
  },
  name: {
    type: String,
    default: () => 'iframe-view',
  },
  // Injected verbatim into the iframe's inline <script>. MUST be a trusted,
  // developer-authored constant — never pass user- or remote-sourced content.
  customScript: {
    type: String,
    default: () => '',
  },
  contentStyles: {
    type: String,
    default: () => '',
  },
  subContentStyles: {
    type: String,
    default: () => '',
  },
  removeSelectors: {
    type: Array,
    default: () => [],
  },
});

const removeSelectors = ref([...[
  '.north',
  '.south',
  '.breadcrumbs',
  '.entry-header',
  '.mo-footer-ctas',
  '#lpage-section-7',
  '#lpage-section-8',
  '#js-find-filter-size-form-pdp',
  '.tags',
  '.page > .inner > h1',
  '.page > h1',
  '.footer-copy',
  '.transmission-download',
  '#jquery-migrate-js',
  '#gform_json-js',
  '#gform_gravityforms-js',
  '#gform_gravityforms_utils-js',
  '#event-move-js',
  '#twenty-twenty-js',
  '#sbav-ajax-js',
  '#wp-polyfill-inert-js',
  '#regenerator-runtime-js',
  '#wp-polyfill-js',
  '#wp-hooks-js',
  '#wp-a11y-js',
  '#wp-i18n-js',
  '#wp-i18n-js-after',
  '#gform_gravityforms_theme_vendors-js',
  '#gform_gravityforms_theme-js-extra',
  '#gform_gravityforms_theme-js',
  '#swp-live-search-client-js-extra',
  '#swp-live-search-client-js',
  '#privy-marketing-widget-js',
  '#privy-marketing-widget-js-extra',
  'a[href*="/contact-us"]',
], ...props.removeSelectors]);

onMounted(() => {
  // Expose the navigation hook so the injected iframe script can drive SPA
  // navigation (keeping fullscreen) instead of reloading the document.
  window[IFRAME_NAV_HOOK] = navigateToLink;
  // Expose the ready hook so the injected script can hide the spinner only once
  // the content is styled and reflowed.
  window[IFRAME_READY_HOOK] = markIframeReady;
  initializeIframe()
  handleSendEmailButton();
});

onUnmounted(() => {
    $(document).off("click", `.js-${props.name}-btn`);
    clearTimeout(readyFallbackTimer);
    // Only clear the hooks if they're still ours — during route transitions the
    // next view can mount before this one unmounts, and we must not clobber its
    // hook (that would silently fall back to a full reload and drop fullscreen).
    if (window[IFRAME_NAV_HOOK] === navigateToLink) delete window[IFRAME_NAV_HOOK];
    if (window[IFRAME_READY_HOOK] === markIframeReady) delete window[IFRAME_READY_HOOK];
});

// Current ?link= as a string ('' at a category root). vue-router yields an
// array for a repeated query key, so collapse that to the first value.
const currentLink = () => {
  const link = route.query.link;
  return (Array.isArray(link) ? link[0] : link) || '';
};

const readStack = () => {
  try {
    return JSON.parse(sessionStorage.getItem(NAV_STACK_KEY) || '[]');
  } catch (err) {
    return [];
  }
};

const writeStack = (stack) => {
  try {
    sessionStorage.setItem(NAV_STACK_KEY, JSON.stringify(stack));
  } catch (err) { /* sessionStorage unavailable */ }
};

// Navigate to a link inside the iframe via the router (no document reload, so
// fullscreen is preserved). Remembers the current page for Back.
const navigateToLink = (href) => {
  if (!href) return;
  const stack = readStack();
  stack.push(currentLink());
  writeStack(stack);
  router.push({ path: route.path, query: { link: href } });
};

// Pop our own navigation stack and SPA-navigate to the previous page. Using the
// router (not a full reload / history.go) keeps fullscreen and avoids the
// iframe-history quirk that made Back require two presses. An empty stack entry
// ('') is a category root, so navigate without the query in that case.
const onBackButtonClick = () => {
  const stack = readStack();
  if (!stack.length) {
    router.push({ path: route.path });
    return;
  }
  const prev = stack.pop();
  writeStack(stack);
  if (prev) {
    router.push({ path: route.path, query: { link: prev } });
  } else {
    router.push({ path: route.path });
  }
};

const handleSendEmailButton = () => {
  $(document).on('click', `.js-${props.name}-btn`, function (e) {
    e.preventDefault;
    
    appStore.createEmailLoading = true;
    appStore.sendEmailBlocked = true;
    appStore.sendEmailRequireBlock = true
    
    QueueService.putIntoQueue({
      link: iframeSrc.value,
      name: route.meta.title,
      screen_number: appStore.screenNumber,
    }, '/data').then((response) => {
      appStore.createEmailLoading = false;
    });
  });
};

const initializeIframe = () => {
  // Make sure the active view owns the hooks (guards against a sibling
  // IframeView's unmount having cleared them during a route transition).
  window[IFRAME_NAV_HOOK] = navigateToLink;
  window[IFRAME_READY_HOOK] = markIframeReady;

  // Reset so the spinner shows and the iframe (v-if) re-creates with the new
  // content when the watcher re-runs this on a SPA link change.
  iframeLoaded.value = false;
  iframeDoc.value = undefined;
  clearTimeout(readyFallbackTimer);

  iframeSrc.value = `${props.url}`;
  // Coerce to a string — vue-router yields an array for a repeated ?link= query.
  const linkParam = currentLink();

  if (linkParam && linkParam !== '' && linkParam != 'undefined') {
    iframeSrc.value = linkParam;
  } else {
    // Landed on a category root — start a fresh navigation stack.
    writeStack([]);
  }
  $(document).ready(function () {
    apiClient.get(websiteProxyUrl(iframeSrc.value)).then(({ data }) => {

      var tempElement = $('<div></div>').html(data);
      tempElement.find(removeSelectors.value.join(', ')).remove();

      removeParents.value.forEach((item) => {
        tempElement.find(item.element).parents(`.${item.parent}`).remove();
      });

      var scriptElement = $('<script><\/script>').text(`
        $(document).ready(function(){
          
          $('html').css('background-color', '#E9E9E9');
          $('html').css('touch-action', 'pan-y');
          $('html').css('scrollbar-width', 'none');
          
          $('.view.page').css('background-color', '#E9E9E9');
          $('.view.page').css('overflow-x', 'hidden');

          $('main.content').css('background-color', '#FFFFFF');

          // The embedded site ships a fixed width:980px desktop layout. Inside the
          // booth frame that overflows horizontally (a horizontal scroll / cut-off
          // content) whenever the iframe's logical width is below 980px — i.e. on a
          // narrow screen, or when zoomed in, since the iframe's logical width is
          // the screen width divided by the zoom factor. Make the layout fluid:
          //  - width:auto + max-width:100% on the fixed-width wrappers so they
          //    shrink to fit a narrow frame and fill a wide one;
          //  - cap every node and the product grid columns to the frame so nothing
          //    can push past the right edge.
          // No per-view contentStyles set widths today, so !important is safe.
          $('head').append(
            '<style>' +
            // The theme forces a min-width on <body> (its fixed ~980px desktop
            // width). That floor is what actually overflows a narrower frame, so
            // reset it first — otherwise the wrappers below just fill 980 again.
            'html, body { min-width: 0 !important; width: auto !important;' +
            ' max-width: 100% !important; overflow-x: hidden !important; }' +
            'main.content, .app, .view, .Palatine, .sub-products, .sub-product-grid,' +
            ' .inner, .Inner { width: auto !important; max-width: 100% !important; }' +
            'main.content, main.content * { max-width: 100% !important;' +
            ' box-sizing: border-box; }' +
            // Equal-size cards: always two equal columns, and equal row heights
            // (grid-auto-rows: 1fr) so every card is the same size regardless of
            // how many land on the last row.
            '.sub-product-grid { display: grid !important;' +
            ' grid-template-columns: repeat(2, 1fr) !important;' +
            ' grid-auto-rows: 1fr !important; gap: 1rem !important;' +
            ' align-items: stretch !important; }' +
            '.sub-product-grid > .sub-product { width: auto !important;' +
            ' min-width: 0 !important; margin: 0 !important; display: flex !important; }' +
            // The white card is an inner element that sizes to its content, so the
            // equal grid cells alone leave the visible cards uneven. Make the inner
            // card fill the cell (row flex + default stretch = full height).
            '.sub-product-grid > .sub-product > .inner-sub-product { flex: 1 1 auto !important; }' +
            // Centre card content ONLY inside the listing grid — the detail page
            // wrapper also carries a .sub-product class, so scope to .sub-product-grid
            // to leave every other page styled as fetched. Text is already centred;
            // block images need auto margins (text-align does not centre a block).
            '.sub-product-grid .sub-product, .sub-product-grid .inner-sub-product {' +
            ' text-align: center !important; }' +
            '.sub-product-grid .sub-product img {' +
            ' margin-left: auto !important; margin-right: auto !important; }' +
            'main.content img { height: auto; }' +
            // Hide the vertical scrollbar but keep the content scrollable
            // (scrollbar-width covers Firefox; ::-webkit-scrollbar covers
            // Chrome/Safari, the booth's engine).
            'html { scrollbar-width: none !important;' +
            ' -ms-overflow-style: none !important; }' +
            'html::-webkit-scrollbar, body::-webkit-scrollbar {' +
            ' width: 0 !important; height: 0 !important; display: none !important; }' +
            '</style>'
          );

          $('.view > .inner').attr('style', '` + props.subContentStyles + `');
          $('.sub-products > .inner').attr('style', '` + props.contentStyles + `');

          $('#privy-widget').remove();

          $('.sub-product, .Application-links, a, .maxbutton').click((e) => {
            e.preventDefault();
            
            const isBtnLink = e.target.className.toLowerCase().includes('btn');
            const isPostImgLink = e.target.className.toLowerCase().includes('wp-post-image');
            const isDetailsLink = ($(e.target).attr('title') || '').toString().toLowerCase().includes('details');
            const isLinkChild = $(e.target).parent()[0].tagName.toLowerCase();
            const isDownloadLink = ($(e.target).attr('title') || '').toString().toLowerCase().includes('download');
            
            if (isDownloadLink) return;
            if (!isBtnLink && !isDetailsLink && !isPostImgLink && !isLinkChild) return;
            
            let href = $(e.target).attr('href') || $(e.target).parent().attr('href');

            if (href.startsWith('/')) href = '` + process.env.VUE_APP_WEBSITE_URL + `' + href;
            
            if (href && href != '' && href != '#' && href !== undefined && !href.startsWith('/')) {
              // Prefer the SPA navigation hook (no document reload → keeps
              // fullscreen and tracks Back). Fall back to a full navigation.
              if (typeof parent.` + IFRAME_NAV_HOOK + ` === 'function') {
                parent.` + IFRAME_NAV_HOOK + `(href);
              } else {
                const originPath = parent.location.origin + parent.location.pathname
                parent.location.href = originPath + '?link=' + href;
              }
            }
          });

          ` + props.customScript + `

          // Signal the booth shell that the content is fully styled AND has been
          // reflowed to the frame, so the spinner is only hidden once the page
          // is actually ready (no flash of the un-resized fixed-width layout).
          // Two rAFs = wait for the style/layout pass to commit before signalling.
          try {
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                if (typeof parent.` + IFRAME_READY_HOOK + ` === 'function') {
                  parent.` + IFRAME_READY_HOOK + `();
                }
              });
            });
          } catch (e) { /* parent gone / cross-origin */ }
        });
    `);
      tempElement.append(scriptElement);
      iframeDoc.value = tempElement.html();

      // Reveal only when the iframe signals ready (see IFRAME_READY_HOOK), with a
      // fallback so a page that never signals still appears.
      clearTimeout(readyFallbackTimer);
      readyFallbackTimer = setTimeout(() => { iframeLoaded.value = true; }, READY_FALLBACK_MS);
    });
  });
};

</script>
<style>
.embedded-component {
  width: 100%;
  /* Fill the space left after the topbar clearance (10rem top padding), the
     Back/email toolbar + gaps, and the 5rem bottom padding (~22rem total) so
     the page itself never scrolls — only the iframe content scrolls.
     Only the vh is divided by --app-zoom (CSS zoom doesn't scale vh); the rem
     overhead is left literal because it already scales with zoom like the
     layout's rem paddings do. */
  height: calc(100vh / var(--app-zoom, 1) - 22rem);
}
</style>